import { useMutation, useQuery, useQueryClient, type QueryKey } from '@tanstack/react-query';
import { ApiError, requisitar, type Query } from '@/lib/api';
import { useSessao } from '@/lib/session';

/**
 * Helpers de TanStack Query. A chave sempre leva a empresa escolhida: trocar de empresa
 * muda as chaves e recarrega os dados (nada de um tenant/empresa vaza para outra tela).
 */
export function useConsulta<T>(
  chave: QueryKey,
  caminho: string,
  opts: { query?: Query; ativo?: boolean; semEmpresa?: boolean } = {},
) {
  const { empresaId } = useSessao();
  const habilitado = (opts.ativo ?? true) && (opts.semEmpresa ? true : empresaId !== null);
  return useQuery<T, ApiError>({
    queryKey: [...chave, opts.semEmpresa ? null : empresaId, opts.query ?? {}],
    queryFn: () => requisitar<T>(caminho, { query: opts.query, semEmpresa: opts.semEmpresa }),
    enabled: habilitado,
  });
}

export interface MutacaoConfig<TVars, TRes> {
  metodo: 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  caminho: (vars: TVars) => string;
  corpo?: (vars: TVars) => unknown;
  /** Prefixos de chave a invalidar apos sucesso. */
  invalidar?: QueryKey[];
  /** true = nao envia X-Empresa-Id (rota @SemEmpresa). */
  semEmpresa?: boolean;
}

export function useMutacao<TRes, TVars = void>(config: MutacaoConfig<TVars, TRes>) {
  const qc = useQueryClient();
  return useMutation<TRes, ApiError, TVars>({
    mutationFn: (vars) =>
      requisitar<TRes>(config.caminho(vars), {
        method: config.metodo,
        body: config.corpo ? config.corpo(vars) : undefined,
        semEmpresa: config.semEmpresa,
      }),
    onSuccess: async () => {
      for (const chave of config.invalidar ?? []) {
        await qc.invalidateQueries({ queryKey: chave });
      }
    },
  });
}
